/**
 * Score Overlay Preview Component
 * 
 * Renders score overlay in the preview player based on configuration.
 * Fetches match data and video tags to build score timeline and match time.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { ScoreOverlayConfig } from '../../../types/overlayConfig';
import { matchDetailsService } from '../../../services/matchDetails';
import { videoTagsService } from '../../../services/videoTagsService';
import { EditorClip } from '../types';

interface ScoreOverlayPreviewProps {
  config: ScoreOverlayConfig;
  matchId?: string;
  clips: EditorClip[];
  currentTime: number; // Current playback time in seconds (from start of timeline)
  playbackMode: 'video' | 'segments';
  aspectRatio?: 'original' | '16:9' | '9:16' | '1:1' | '4:3';
}

interface MatchData {
  homeTeam: { id: string; name: string; logo?: string } | null;
  awayTeam: { id: string; name: string; logo?: string } | null;
  homeScore: number;
  awayScore: number;
  tournament: { id: string; name: string; logo?: string } | null;
}

interface ScoreEvent {
  videoTimeSeconds: number;
  matchMinute: number;
  teamId: string;
  isHomeTeam: boolean;
  videoId: string; // Add videoId to match goals to clips
}

interface MatchTimeEvent {
  videoTimeSeconds: number;
  matchMinute: number;
}

const ScoreOverlayPreview: React.FC<ScoreOverlayPreviewProps> = React.memo(({
  config,
  matchId,
  clips,
  currentTime,
  playbackMode,
  aspectRatio = 'original',
}) => {
  const [matchData, setMatchData] = useState<MatchData | null>(null);
  const [scoreEvents, setScoreEvents] = useState<ScoreEvent[]>([]);
  const [matchTimeEvents, setMatchTimeEvents] = useState<MatchTimeEvent[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch match data and video tags
  useEffect(() => {
    if (!matchId || !config.enabled) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch match details
        const matchDetails = await matchDetailsService.getMatchDetails(matchId);
        if (!matchDetails) {
          setLoading(false);
          return;
        }

        const homeTeam = matchDetails.home_team as any;
        const awayTeam = matchDetails.away_team as any;
        const tournament = matchDetails.tournament as any;

        setMatchData({
          homeTeam: homeTeam ? { id: homeTeam.id, name: homeTeam.name, logo: homeTeam.logo } : null,
          awayTeam: awayTeam ? { id: awayTeam.id, name: awayTeam.name, logo: awayTeam.logo } : null,
          homeScore: matchDetails.home_score || 0,
          awayScore: matchDetails.away_score || 0,
          tournament: tournament ? { id: tournament.id, name: tournament.name, logo: tournament.logo } : null,
        });

        // Fetch video tags for score timeline and match time
        const videoIds = clips.map(clip => clip.sourceVideoId).filter(Boolean);
        if (videoIds.length === 0) {
          setLoading(false);
          return;
        }

        // Get all video tags for the match
        const tagsResult = await videoTagsService.getVideoTags(matchId);
        if (!tagsResult.success || !tagsResult.data) {
          setLoading(false);
          return;
        }

        // Filter tags for videos in our clips
        const relevantTags = tagsResult.data.filter(tag => 
          tag.video_id && videoIds.includes(tag.video_id) &&
          tag.video_time_seconds !== null
        );

        // Build score timeline from goal events
        const goalStatKeys = ['shot_inside_box_goal', 'shot_outside_box_goal', 'penalty_scored', 'own_goal'];
        const goalEvents: ScoreEvent[] = [];
        
        relevantTags.forEach(tag => {
          const statKey = tag.stat_type?.stat_key;
          if (statKey && goalStatKeys.includes(statKey) && tag.team_id && tag.match_minute !== null && tag.video_id) {
            goalEvents.push({
              videoTimeSeconds: tag.video_time_seconds!,
              matchMinute: tag.match_minute,
              teamId: tag.team_id,
              isHomeTeam: tag.team_id === homeTeam?.id,
              videoId: tag.video_id,
            });
          }
        });

        // Sort by match minute
        goalEvents.sort((a, b) => a.matchMinute - b.matchMinute);
        setScoreEvents(goalEvents);

        // Build match time mapping from all events
        const timeEvents: MatchTimeEvent[] = [];
        relevantTags.forEach(tag => {
          if (tag.match_minute !== null && tag.video_time_seconds !== null) {
            timeEvents.push({
              videoTimeSeconds: tag.video_time_seconds,
              matchMinute: tag.match_minute,
            });
          }
        });

        // Sort by video time
        timeEvents.sort((a, b) => a.videoTimeSeconds - b.videoTimeSeconds);
        setMatchTimeEvents(timeEvents);

        setLoading(false);
      } catch (error) {
        console.error('Error fetching score overlay data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, [matchId, config.enabled, clips]);

  // Calculate position styles - MUST be before any early returns (React Hooks rules)
  // Positioned relative to the aspect-ratio-constrained container (not original video)
  // Adjust padding based on aspect ratio
  const positionStyles = useMemo(() => {
    const basePosition = config.position || 'top-right';
    type PositionStyle = { top?: string; bottom?: string; left?: string; right?: string; transform?: string };
    
    // Position map including top/bottom centered options
    const positionMap: Record<string, PositionStyle> = {
      'top-left': { top: '16px', left: '16px' },
      'top-right': { top: '16px', right: '16px' },
      'top': { top: '16px', left: '50%', transform: 'translateX(-50%)' }, // Centered at top
      'bottom-left': { bottom: '16px', left: '16px' },
      'bottom-right': { bottom: '16px', right: '16px' },
      'bottom': { bottom: '16px', left: '50%', transform: 'translateX(-50%)' }, // Centered at bottom
    };

    // Adjust padding for vertical (9:16) - reduce horizontal padding
    if (aspectRatio === '9:16') {
      const verticalPositionMap: Record<string, PositionStyle> = {
        'top-left': { top: '16px', left: '8px' },
        'top-right': { top: '16px', right: '8px' },
        'top': { top: '16px', left: '50%', transform: 'translateX(-50%)' },
        'bottom-left': { bottom: '16px', left: '8px' },
        'bottom-right': { bottom: '16px', right: '8px' },
        'bottom': { bottom: '16px', left: '50%', transform: 'translateX(-50%)' },
      };
      return verticalPositionMap[basePosition] || positionMap[basePosition] || positionMap['top-right'];
    }

    // Adjust padding for square (1:1) - balanced padding
    if (aspectRatio === '1:1') {
      const squarePositionMap: Record<string, PositionStyle> = {
        'top-left': { top: '12px', left: '12px' },
        'top-right': { top: '12px', right: '12px' },
        'top': { top: '12px', left: '50%', transform: 'translateX(-50%)' },
        'bottom-left': { bottom: '12px', left: '12px' },
        'bottom-right': { bottom: '12px', right: '12px' },
        'bottom': { bottom: '12px', left: '50%', transform: 'translateX(-50%)' },
      };
      return squarePositionMap[basePosition] || positionMap[basePosition] || positionMap['top-right'];
    }

    // Default for original, 16:9, 4:3
    return positionMap[basePosition] || positionMap['top-right'];
  }, [config.position, aspectRatio]);

  // Generate gradient CSS - MUST be before any early returns
  const gradientStyle = useMemo(() => {
    if (!config.gradient?.enabled) {
      return undefined;
    }

    const startColor = config.gradient.startColor || config.colors.primary;
    const endColor = config.gradient.endColor || config.colors.secondary;

    if (config.gradient.type === 'radial') {
      const centerX = config.gradient.stops?.[0] ?? 50;
      const centerY = config.gradient.stops?.[1] ?? 50;
      return `radial-gradient(circle at ${centerX}% ${centerY}%, ${startColor}, ${endColor})`;
    } else {
      // horizontal or vertical with direction
      const angle = config.gradient.direction ?? 0;
      return `linear-gradient(${angle}deg, ${startColor}, ${endColor})`;
    }
  }, [config.gradient, config.colors.primary, config.colors.secondary]);

  // Map timeline/video position to clip and get match minute - MUST be before any early returns
  // Handles both 'segments' mode (timeline position) and 'video' mode (stitched video time)
  const currentClipInfo = useMemo(() => {
    if (clips.length === 0) {
      return { clip: null, matchMinute: null, videoTime: null, offsetInClip: 0 };
    }

    if (playbackMode === 'video') {
      // In stitched video mode, currentTime is the time in the stitched video
      // Map it back to which segment it corresponds to
      let accumulatedTime = 0;
      for (const clip of clips) {
        const clipDuration = clip.endTime - clip.startTime;
        if (currentTime >= accumulatedTime && currentTime < accumulatedTime + clipDuration) {
          const offsetInClip = currentTime - accumulatedTime;
          // Map to actual video time in source video
          const videoTime = clip.startTime + offsetInClip;
          return {
            clip,
            matchMinute: clip.metadata?.matchMinute ?? null,
            videoTime,
            offsetInClip,
          };
        }
        accumulatedTime += clipDuration;
      }
      // If past all clips, use the last clip
      const lastClip = clips[clips.length - 1];
      const lastClipDuration = lastClip.endTime - lastClip.startTime;
      return {
        clip: lastClip,
        matchMinute: lastClip.metadata?.matchMinute ?? null,
        videoTime: lastClip.endTime,
        offsetInClip: lastClipDuration,
      };
    } else {
      // In segments mode, currentTime is timeline position
      let accumulatedTime = 0;
      for (const clip of clips) {
        const clipDuration = clip.endTime - clip.startTime;
        if (currentTime >= accumulatedTime && currentTime <= accumulatedTime + clipDuration) {
          const offsetInClip = currentTime - accumulatedTime;
          // Map to actual video time in source video
          const videoTime = clip.startTime + offsetInClip;
          return {
            clip,
            matchMinute: clip.metadata?.matchMinute ?? null,
            videoTime,
            offsetInClip,
          };
        }
        accumulatedTime += clipDuration;
      }
      // If past all clips, use the last clip
      const lastClip = clips[clips.length - 1];
      const lastClipDuration = lastClip.endTime - lastClip.startTime;
      return {
        clip: lastClip,
        matchMinute: lastClip.metadata?.matchMinute ?? null,
        videoTime: lastClip.endTime,
        offsetInClip: lastClipDuration,
      };
    }
  }, [clips, currentTime, playbackMode]);

  // Get current match minute - MUST be before any early returns
  const currentMatchMinute = useMemo(() => {
    if (!config.showMatchTime) {
      return null;
    }

    // Use match minute from current clip if available
    if (currentClipInfo.matchMinute !== null) {
      return currentClipInfo.matchMinute;
    }

    // Fallback: try to find match minute from matchTimeEvents based on video time
    if (matchTimeEvents.length === 0 || currentClipInfo.videoTime === null) {
      return null;
    }

    // Find closest match time event based on actual video time
    const closestEvent = matchTimeEvents.reduce((closest, event) => {
      if (!closest) return event;
      const closestDiff = Math.abs(closest.videoTimeSeconds - currentClipInfo.videoTime!);
      const eventDiff = Math.abs(event.videoTimeSeconds - currentClipInfo.videoTime!);
      return eventDiff < closestDiff ? event : closest;
    }, null as MatchTimeEvent | null);

    return closestEvent ? closestEvent.matchMinute : null;
  }, [config.showMatchTime, currentClipInfo, matchTimeEvents]);

  // Calculate current score based on timeline position - MUST be before any early returns
  // This works in REAL-TIME during preview: as currentTime updates during playback,
  // the score and match minute update automatically based on the current clip's match minute
  // and goals that occurred before or at that minute.
  // After worker processing, the same logic will be applied frame-by-frame during video rendering.
  // 
  // Goal timing: Goals are only counted when we're past the actual goal event time.
  // For goal segments: count the goal when we're at video_time_seconds + 1 second, or 2-3 seconds before clip end.
  const currentScore = useMemo(() => {
    if (!matchData) {
      return {
        home: 0,
        away: 0,
      };
    }

    // If not updating on goals, show final score
    if (!config.updateOnGoals) {
      return {
        home: matchData.homeScore,
        away: matchData.awayScore,
      };
    }

    // Get current clip info
    const clip = currentClipInfo.clip;
    const videoTime = currentClipInfo.videoTime;
    const offsetInClip = currentClipInfo.offsetInClip;
    
    if (!clip || videoTime === null) {
      // Fallback to final score if we can't determine position
      return {
        home: matchData.homeScore,
        away: matchData.awayScore,
      };
    }

    // Calculate score based on goals that occurred before the current point
    let homeScore = 0;
    let awayScore = 0;

    scoreEvents.forEach(event => {
      // Check if this goal should be counted
      let shouldCount = false;
      
      // Check if this goal belongs to the current clip
      const isGoalInCurrentClip = event.videoId === clip.sourceVideoId &&
                                   event.videoTimeSeconds >= clip.startTime &&
                                   event.videoTimeSeconds <= clip.endTime;
      
      if (isGoalInCurrentClip) {
        // For goals in the current clip, only count when we're past the goal event time + 1 second
        // OR if we're 2-3 seconds before the clip end
        const clipDuration = clip.endTime - clip.startTime;
        const timeUntilClipEnd = clipDuration - offsetInClip;
        const goalTimeInClip = event.videoTimeSeconds - clip.startTime;
        
        // Count if we're past the goal time + 1 second, or 2-3 seconds before clip end
        shouldCount = (offsetInClip >= goalTimeInClip + 1) || (timeUntilClipEnd <= 3 && timeUntilClipEnd >= 2);
      } else {
        // Goal is in a different clip - count if it occurred before current match minute
        const matchMinute = currentMatchMinute;
        if (matchMinute !== null && event.matchMinute < matchMinute) {
          shouldCount = true;
        }
      }
      
      if (shouldCount) {
        if (event.isHomeTeam) {
          homeScore++;
        } else {
          awayScore++;
        }
      }
    });

    return {
      home: homeScore,
      away: awayScore,
    };
  }, [matchData, config.updateOnGoals, currentClipInfo, scoreEvents, currentMatchMinute]);

  // Early return after all hooks
  if (!config.enabled || loading || !matchData) {
    return null;
  }

  // Render based on preset
  if (config.preset === 'preset1') {
    // Improved Preset 1: Compact card with better visual hierarchy
    const backgroundStyle = gradientStyle 
      ? { background: gradientStyle }
      : { backgroundColor: config.colors.secondary };

    return (
      <div
        className="absolute z-20 pointer-events-none"
        style={{
          ...positionStyles,
          ...backgroundStyle,
          opacity: config.opacity ?? 1.0,
          color: config.colors.textColor,
          // Size-based padding and font sizes
          padding: config.size === 'xs' ? '6px 8px' : config.size === 'sm' ? '8px 10px' : config.size === 'lg' ? '12px 16px' : '10px 14px',
          borderRadius: '0px', // No rounded corners
          fontSize: config.size === 'xs' ? '10px' : config.size === 'sm' ? '11px' : config.size === 'lg' ? '14px' : '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
          backdropFilter: 'blur(4px)',
          ...(positionStyles.transform ? { transform: positionStyles.transform } : {}),
        }}
      >
        <div className={`flex items-center ${config.size === 'xs' ? 'space-x-2' : config.size === 'sm' ? 'space-x-3' : config.size === 'lg' ? 'space-x-5' : 'space-x-4'} font-medium`}>
          {/* Home Team */}
          <div className={`flex items-center ${config.size === 'xs' ? 'space-x-1' : config.size === 'sm' ? 'space-x-1.5' : 'space-x-2'}`}>
            {matchData.homeTeam?.logo && (
              <img
                src={matchData.homeTeam.logo}
                alt={matchData.homeTeam.name}
                className={`${config.size === 'xs' ? 'w-4 h-4' : config.size === 'sm' ? 'w-4.5 h-4.5' : config.size === 'lg' ? 'w-6 h-6' : 'w-5 h-5'} object-contain`}
                style={{ width: config.size === 'xs' ? '16px' : config.size === 'sm' ? '18px' : config.size === 'lg' ? '24px' : '20px', height: config.size === 'xs' ? '16px' : config.size === 'sm' ? '18px' : config.size === 'lg' ? '24px' : '20px' }}
              />
            )}
            <span className={`truncate ${config.size === 'xs' ? 'max-w-[60px] text-[10px]' : config.size === 'sm' ? 'max-w-[70px] text-[11px]' : config.size === 'lg' ? 'max-w-[110px] text-[14px]' : 'max-w-[90px] text-xs'} font-semibold`} style={{ color: config.colors.textColor }}>{matchData.homeTeam?.name || 'Home'}</span>
            <span
              style={{ color: config.colors.primary, display: 'inline-block' }}
              className={`font-bold ${config.size === 'xs' ? 'text-sm' : config.size === 'sm' ? 'text-base' : config.size === 'lg' ? 'text-lg' : 'text-base'} ml-1`}
            >
              {currentScore.home}
            </span>
          </div>

          {/* Separator */}
          <span style={{ color: config.colors.textColor, opacity: 0.4 }} className={`${config.size === 'xs' ? 'px-0.5' : 'px-1'}`}>•</span>

          {/* Away Team */}
          <div className={`flex items-center ${config.size === 'xs' ? 'space-x-1' : config.size === 'sm' ? 'space-x-1.5' : 'space-x-2'}`}>
            <span
              style={{ color: config.colors.primary, display: 'inline-block' }}
              className={`font-bold ${config.size === 'xs' ? 'text-sm' : config.size === 'sm' ? 'text-base' : config.size === 'lg' ? 'text-lg' : 'text-base'} mr-1`}
            >
              {currentScore.away}
            </span>
            {matchData.awayTeam?.logo && (
              <img
                src={matchData.awayTeam.logo}
                alt={matchData.awayTeam.name}
                className={`${config.size === 'xs' ? 'w-4 h-4' : config.size === 'sm' ? 'w-4.5 h-4.5' : config.size === 'lg' ? 'w-6 h-6' : 'w-5 h-5'} object-contain`}
                style={{ width: config.size === 'xs' ? '16px' : config.size === 'sm' ? '18px' : config.size === 'lg' ? '24px' : '20px', height: config.size === 'xs' ? '16px' : config.size === 'sm' ? '18px' : config.size === 'lg' ? '24px' : '20px' }}
              />
            )}
            <span className={`truncate ${config.size === 'xs' ? 'max-w-[60px] text-[10px]' : config.size === 'sm' ? 'max-w-[70px] text-[11px]' : config.size === 'lg' ? 'max-w-[110px] text-[14px]' : 'max-w-[90px] text-xs'} font-semibold`} style={{ color: config.colors.textColor }}>{matchData.awayTeam?.name || 'Away'}</span>
          </div>

          {/* Tournament Logo (center) */}
          {config.showTournamentLogo && matchData.tournament?.logo && (
            <div className={`flex items-center ${config.size === 'xs' ? 'px-1' : config.size === 'sm' ? 'px-1.5' : config.size === 'lg' ? 'px-3' : 'px-2'} border-l`} style={{ borderColor: `${config.colors.textColor}40` }}>
              <img
                src={matchData.tournament.logo}
                alt={matchData.tournament.name}
                className={`${config.size === 'xs' ? 'w-4 h-4' : config.size === 'sm' ? 'w-5 h-5' : config.size === 'lg' ? 'w-7 h-7' : 'w-6 h-6'} object-contain`}
                style={{ width: config.size === 'xs' ? '16px' : config.size === 'sm' ? '20px' : config.size === 'lg' ? '28px' : '24px', height: config.size === 'xs' ? '16px' : config.size === 'sm' ? '20px' : config.size === 'lg' ? '28px' : '24px' }}
              />
            </div>
          )}

          {/* Match Time */}
          {config.showMatchTime && currentMatchMinute !== null && (
            <div className={`flex items-center ${config.size === 'xs' ? 'px-1' : config.size === 'sm' ? 'px-1.5' : config.size === 'lg' ? 'px-3' : 'px-2'} border-l`} style={{ borderColor: `${config.colors.textColor}40` }}>
              <span className={`${config.size === 'xs' ? 'text-[10px]' : config.size === 'sm' ? 'text-[11px]' : config.size === 'lg' ? 'text-sm' : 'text-xs'} font-semibold`}>{currentMatchMinute}'</span>
            </div>
          )}
        </div>
      </div>
    );
  } else {
    // Improved Preset 2: Full-width bar with better spacing and design
    // Color definitions (consistent with Preset 1):
    // Primary color = Score color (home/away scores)
    // Secondary color = Background color (container background)
    // Text color = Text color (team names, match time, etc.)
    const backgroundStyle = gradientStyle 
      ? { background: gradientStyle }
      : { backgroundColor: config.colors.secondary };

    return (
      <div
        className="absolute z-20 pointer-events-none w-full"
        style={{
          ...(config.position.startsWith('bottom') ? { bottom: 0 } : { top: 0 }),
          left: 0,
          right: 0,
          ...backgroundStyle,
          opacity: config.opacity ?? 1.0,
          color: config.colors.textColor,
          // Size-based padding
          padding: config.size === 'xs' ? '8px 12px' : config.size === 'sm' ? '10px 16px' : config.size === 'lg' ? '18px 24px' : '14px 20px',
          fontSize: config.size === 'xs' ? '10px' : config.size === 'sm' ? '11px' : config.size === 'lg' ? '16px' : '14px',
          boxShadow: config.position.startsWith('bottom') 
            ? '0 -4px 12px rgba(0,0,0,0.4)' 
            : '0 4px 12px rgba(0,0,0,0.4)',
          backdropFilter: 'blur(4px)',
        }}
      >
        <div className={`flex items-center justify-between max-w-6xl mx-auto ${config.size === 'xs' ? 'space-x-2' : config.size === 'sm' ? 'space-x-3' : config.size === 'lg' ? 'space-x-8' : 'space-x-6'}`}>
          {/* Home Team */}
          <div className={`flex items-center ${config.size === 'xs' ? 'space-x-2' : config.size === 'sm' ? 'space-x-3' : config.size === 'lg' ? 'space-x-5' : 'space-x-4'}`}>
            {matchData.homeTeam?.logo && (
              <img
                src={matchData.homeTeam.logo}
                alt={matchData.homeTeam.name}
                className="object-contain"
                style={{ width: config.size === 'xs' ? '24px' : config.size === 'sm' ? '32px' : config.size === 'lg' ? '48px' : '40px', height: config.size === 'xs' ? '24px' : config.size === 'sm' ? '32px' : config.size === 'lg' ? '48px' : '40px' }}
              />
            )}
            <div className="flex flex-col">
              <span className={`${config.size === 'xs' ? 'text-[10px]' : config.size === 'sm' ? 'text-[11px]' : config.size === 'lg' ? 'text-sm' : 'text-xs'} opacity-90 font-medium`} style={{ color: config.colors.textColor }}>{matchData.homeTeam?.name || 'Home'}</span>
              <span 
                style={{ color: config.colors.primary }}
                className={`${config.size === 'xs' ? 'text-base' : config.size === 'sm' ? 'text-lg' : config.size === 'lg' ? 'text-2xl' : 'text-xl'} font-bold`}
              >
                {currentScore.home}
              </span>
            </div>
          </div>

          {/* Center: Tournament Logo and Match Time */}
          <div className={`flex items-center ${config.size === 'xs' ? 'space-x-3' : config.size === 'sm' ? 'space-x-4' : config.size === 'lg' ? 'space-x-8' : 'space-x-6'}`}>
            {config.showTournamentLogo && matchData.tournament?.logo && (
              <img
                src={matchData.tournament.logo}
                alt={matchData.tournament.name}
                className="object-contain"
                style={{ width: config.size === 'xs' ? '28px' : config.size === 'sm' ? '36px' : config.size === 'lg' ? '56px' : '48px', height: config.size === 'xs' ? '28px' : config.size === 'sm' ? '36px' : config.size === 'lg' ? '56px' : '48px' }}
              />
            )}
            {config.showMatchTime && currentMatchMinute !== null && (
              <div className={`${config.size === 'xs' ? 'px-2 py-1' : config.size === 'sm' ? 'px-3 py-1' : config.size === 'lg' ? 'px-5 py-2' : 'px-4 py-1.5'}`} style={{ backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '0px' }}>
                <span className={`${config.size === 'xs' ? 'text-[10px]' : config.size === 'sm' ? 'text-[11px]' : config.size === 'lg' ? 'text-base' : 'text-sm'} font-semibold`}>{currentMatchMinute}'</span>
              </div>
            )}
          </div>

          {/* Away Team */}
          <div className={`flex items-center ${config.size === 'xs' ? 'space-x-2' : config.size === 'sm' ? 'space-x-3' : config.size === 'lg' ? 'space-x-5' : 'space-x-4'}`}>
            <div className="flex flex-col text-right">
              <span className={`${config.size === 'xs' ? 'text-[10px]' : config.size === 'sm' ? 'text-[11px]' : config.size === 'lg' ? 'text-sm' : 'text-xs'} opacity-90 font-medium`} style={{ color: config.colors.textColor }}>{matchData.awayTeam?.name || 'Away'}</span>
              <span 
                style={{ color: config.colors.primary }}
                className={`${config.size === 'xs' ? 'text-base' : config.size === 'sm' ? 'text-lg' : config.size === 'lg' ? 'text-2xl' : 'text-xl'} font-bold`}
              >
                {currentScore.away}
              </span>
            </div>
            {matchData.awayTeam?.logo && (
              <img
                src={matchData.awayTeam.logo}
                alt={matchData.awayTeam.name}
                className="object-contain"
                style={{ width: config.size === 'xs' ? '24px' : config.size === 'sm' ? '32px' : config.size === 'lg' ? '48px' : '40px', height: config.size === 'xs' ? '24px' : config.size === 'sm' ? '32px' : config.size === 'lg' ? '48px' : '40px' }}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
});

ScoreOverlayPreview.displayName = 'ScoreOverlayPreview';

export default ScoreOverlayPreview;

